import { primaryAppPath } from "./appNavigation";

export const SIGN_IN_PATH = "/register?auth=sign-in";

const MAX_RETURN_TO_LENGTH = 512;

// Same-origin app paths only. Rejects protocol-relative ("//host"), backslash
// ("/\host" is read as "//host" by browsers), whitespace and control characters.
export const safeReturnToPath = (value) => {
  if (typeof value !== 'string' || !value) return null;
  if (value.length > MAX_RETURN_TO_LENGTH) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\s\u0000-\u001f\u007f]/.test(value)) return null;

  return value;
};

export const returnToFromSearch = (search = '') =>
  safeReturnToPath(new URLSearchParams(search).get('return_to'));

// `path` may already carry a query string (e.g. SIGN_IN_PATH).
export const withReturnTo = (path, returnTo) => {
  const safeReturnTo = safeReturnToPath(returnTo);
  if (!safeReturnTo) return path;

  const [pathname, query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  params.set('return_to', safeReturnTo);
  return `${pathname}?${params.toString()}`;
};

export const signInPathFor = (returnTo) => withReturnTo(SIGN_IN_PATH, returnTo);
export const signUpPathFor = (returnTo) => withReturnTo('/register', returnTo);
export const confirmEmailPathFor = (returnTo) => withReturnTo('/confirm-email', returnTo);
export const forgotPasswordPathFor = (returnTo) => withReturnTo('/forgot-password', returnTo);

// Post-auth navigation for return_to targets. /start-checkout is a Rails
// endpoint, not an SPA route: it needs a full-page navigation so the request
// carries the fresh session cookie and can 303 straight to Stripe Checkout.
export const navigateToPostAuthPath = (navigate, path) => {
  const target = safeReturnToPath(path) || primaryAppPath();

  if (target.startsWith('/start-checkout')) {
    window.location.assign(target);
    return;
  }

  navigate(target);
};

export const registerShouldStartWithSignIn = ({ search = '', uuidHasAccount = false } = {}) => (
  !!uuidHasAccount || new URLSearchParams(search).get('auth') === 'sign-in'
);

export const existingAccountSignInPath = ({
  pathname = '/plan-selection',
  search = '',
  hash = '',
} = {}) => {
  const returnTo = safeReturnToPath(`${pathname}${search}${hash}`) || '/plan-selection';
  const params = new URLSearchParams({
    auth: 'sign-in',
    return_to: returnTo,
  });

  return `/register?${params.toString()}`;
};

// One-tap shortcut to the inbox that holds a verification or reset email.
const WEBMAIL_BY_DOMAIN = [
  { domains: ['gmail.com', 'googlemail.com'], label: 'Open Gmail', url: 'https://mail.google.com/mail/u/0/#search/from%3Ainciteai.com+in%3Aanywhere+newer_than%3A1d' },
  { domains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'], label: 'Open Outlook', url: 'https://outlook.live.com/mail/0/' },
  { domains: ['yahoo.com', 'ymail.com'], label: 'Open Yahoo Mail', url: 'https://mail.yahoo.com/' },
  { domains: ['icloud.com', 'me.com', 'mac.com'], label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail' },
  { domains: ['proton.me', 'protonmail.com'], label: 'Open Proton Mail', url: 'https://mail.proton.me/' },
  { domains: ['aol.com'], label: 'Open AOL Mail', url: 'https://mail.aol.com/' },
];

export const webmailShortcutFor = (email) => {
  const domain = String(email || '').trim().toLowerCase().split('@')[1];
  if (!domain) return null;

  const match = WEBMAIL_BY_DOMAIN.find((entry) => entry.domains.includes(domain));
  return match ? { label: match.label, url: match.url } : null;
};
